import React, { FC, useMemo } from 'react'
import * as Styles from './segmentIndicator.module.scss'

/**
 * Props for the `SegmentIndicator` component.
 */
export type SegmentIndicatorProps = {
  segmentCount: number
  currentIndex: number
}

/**
 * A component that visually indicates the current segment in a track of segments.
 *
 * Typically used to show progress or position within a series of segments, such as in a carousel or slider.
 */
export const SegmentIndicator: FC<SegmentIndicatorProps> = ({
  segmentCount,
  currentIndex,
}) => {
  /**
   * The width of each segment as a percentage of the total width of the track.
   */
  const currentSegmentWidthPercentage = useMemo(() => {
    if (segmentCount <= 0) {
      return 0
    }

    return 100 / segmentCount
  }, [segmentCount])

  /**
   * The offset of the current segment as a percentage of the total width of the track, measured from the starting edge
   * of the track to the starting edge of the current segment.
   */
  const currentSegmentOffsetPercentage = useMemo(() => {
    if (segmentCount <= 0) {
      return 0
    }

    return currentIndex * currentSegmentWidthPercentage
  }, [currentIndex, currentSegmentWidthPercentage])

  /**
   * The style object for the current segment, which includes its width and transform properties.
   */
  const currentSegmentStyle = useMemo(() => {
    return {
      width: `${currentSegmentWidthPercentage}%`,
      insetInlineStart: `${currentSegmentOffsetPercentage}%`,
    }
  }, [currentSegmentWidthPercentage, currentSegmentOffsetPercentage])

  /**
   * Whether the current segment should be rendered.
   */
  const shouldRenderCurrentSegment = useMemo(() => {
    return currentIndex >= 0 && currentIndex < segmentCount
  }, [segmentCount, currentIndex])

  return (
    <div className={Styles.track} data-testid="segment-indicator-track">
      {shouldRenderCurrentSegment && (
        <div
          className={Styles.currentSegment}
          style={currentSegmentStyle}
          data-testid="segment-indicator-current-segment"
        />
      )}
    </div>
  )
}
